/* Created by Interactive 63 - https://interactive63.com */
/**
 * The stage overlay: an SVG drawn above the stage in screen space, so line widths stay constant
 * at every zoom level. It draws selection and hover outlines (the element's own box carried
 * through its transforms), the transform origin marker, scale and rotate handles, the motion
 * path, snap guides, the pivot rulers and the live readout. It only draws what it is given: the
 * measurements and paths are computed elsewhere.
 */
import type { NodeRef, SegmentRef } from '../../../core/path/edit';
import type { PointEditData } from './point-edit';
import type { ElementId } from '../../../core/model/types';
import { svgEl } from '../../ui/dom';
import type { ArtboardHandle } from '../../../core/model/artboard';
import { angleOf, resizeCursor, type Point, type ScaleAxes } from './geometry';

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export interface RulerLine {
  direction: 'left' | 'right' | 'up' | 'down';
  /** The pivot and the edge reached, in client coordinates. */
  from: Point;
  to: Point;
  label: string;
}

export interface Ruler {
  target: 'layer' | 'shape' | 'neighbour' | 'artboard';
  lines: RulerLine[];
  centredX: boolean;
  centredY: boolean;
}

export interface MotionPathData {
  elementId: ElementId;
  /** One screen point per sampled frame. */
  points: Point[];
  /** Keyframe positions, with the ends of their curve handles where they have them. */
  keys: { id: string; point: Point; inHandle?: Point; outHandle?: Point }[];
}

export interface MorphGuides {
  /** Subpath start points of the previous shape and of the selected one, in client coordinates. */
  before: Point[];
  after: Point[];
  /** Pairs of corresponding points along the outline. */
  links: [Point, Point][];
}

export interface OverlayScene {
  rulers: Ruler[];
  /** Client x positions of vertical guides and y positions of horizontal ones. */
  guidesX: number[];
  guidesY: number[];
  readout: { text: string; x: number; y: number } | null;
  motion: MotionPathData | null;
  morph: MorphGuides | null;
  /** The points of the path being edited, when point editing is on. */
  points: PointEditData | null;
}

export const EMPTY_SCENE: OverlayScene = {
  rulers: [],
  guidesX: [],
  guidesY: [],
  readout: null,
  motion: null,
  morph: null,
  points: null,
};

export interface OverlayHost {
  /** Finds the rendered stage element for an element ID. */
  getElement(id: ElementId): Element | undefined;
  /** Finds rendered elements painted with the gradient that owns a stop. */
  getGradientUsers(stopId: ElementId): Element[];
  /** Whether the element is a gradient stop, which cannot be outlined directly. */
  isStop(id: ElementId): boolean;
  /** Where the element pivots, in client coordinates, or null when it cannot be measured. */
  getOrigin(id: ElementId): { x: number; y: number } | null;
  /** The four corners of an element's bounding box on screen, following its transforms. */
  getQuad(element: Element): { x: number; y: number }[] | null;
  /** Starts dragging an element's origin from a pointer event on its marker. */
  beginOriginDrag(id: ElementId, event: PointerEvent): void;
  /** Puts an element's origin back at the centre of its bounding box. */
  resetOrigin(id: ElementId): void;
  /** Opens the marker's context menu. */
  showOriginMenu(id: ElementId, x: number, y: number): void;
  /** Starts rotating the selection from the rotate handle. */
  beginRotate(id: ElementId, event: PointerEvent): void;
  /** Starts scaling the selection from a scale handle. */
  beginScale(id: ElementId, axes: ScaleAxes, event: PointerEvent): void;
  /** The artboard's rectangle on screen, in client coordinates. */
  getArtboardRect(): { left: number; top: number; right: number; bottom: number } | null;
  /** Starts resizing the artboard from one of its handles. */
  beginArtboardResize(handle: ArtboardHandle, event: PointerEvent): void;
  /** Starts dragging a keyframe square on the motion path. */
  beginMotionKeyDrag(elementId: ElementId, keyframeId: string, event: PointerEvent): void;
  /** Starts dragging one curve handle of a motion path keyframe. */
  beginMotionHandleDrag(
    elementId: ElementId,
    keyframeId: string,
    side: 'in' | 'out',
    event: PointerEvent,
  ): void;
  /** Gives a straight motion path keyframe smooth handles, or straightens a curved one. */
  toggleMotionSmooth(keyframeId: string): void;
  /** A press on a point of the path being edited. */
  beginPointDrag(ref: NodeRef, event: PointerEvent): void;
  /** A press on a curve handle of the path being edited. */
  beginPointHandleDrag(ref: NodeRef, side: 'in' | 'out', event: PointerEvent): void;
  /** Adds a point where a segment of the path being edited was double-clicked. */
  insertPoint(seg: SegmentRef, clientX: number, clientY: number): void;
}

const ROTATE_HANDLE_OFFSET = 24;
const EDGE_HANDLE_MIN_LENGTH = 40;
const HANDLE_SIZE = 8;

/** The unit vector from a to b, or zero when they coincide. */
function direction(a: Point, b: Point): Point {
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  return length === 0 ? { x: 0, y: 0 } : { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
}

export class Overlay {
  readonly el: SVGSVGElement;

  /** Creates the overlay for a viewport. The viewport supplies the coordinate origin. */
  constructor(
    private readonly viewport: HTMLElement,
    private readonly host: OverlayHost,
  ) {
    this.el = svgEl('svg', {
      class: 'stage-overlay',
      role: 'group',
      'aria-label': 'Selection outline and handles',
    });
  }

  /** Draws outlines, handles and extras for the given selection, hover and scene. */
  draw(
    selectedIds: readonly ElementId[],
    hoveredId: ElementId | null,
    scene: OverlayScene = EMPTY_SCENE,
  ): void {
    const focusedMarker =
      document.activeElement instanceof Element && this.el.contains(document.activeElement)
        ? document.activeElement.getAttribute('data-origin-for')
        : null;
    this.el.replaceChildren();
    const origin = this.viewport.getBoundingClientRect();
    /** Converts a client point to overlay coordinates. */
    const local = (p: Point): Point => ({ x: p.x - origin.left, y: p.y - origin.top });

    /** Appends an outline for a rendered element: its box through its transforms. */
    const outline = (target: Element, className: string): void => {
      const quad = this.host.getQuad(target);
      if (quad) {
        this.el.append(
          svgEl('polygon', {
            class: className,
            points: quad.map((p) => `${p.x - origin.left},${p.y - origin.top}`).join(' '),
          }),
        );
        return;
      }
      const box = target.getBoundingClientRect();
      if (box.width === 0 && box.height === 0) return;
      this.el.append(
        svgEl('rect', {
          class: className,
          x: box.left - origin.left,
          y: box.top - origin.top,
          width: box.width,
          height: box.height,
        }),
      );
    };

    if (hoveredId && !selectedIds.includes(hoveredId) && !this.host.isStop(hoveredId)) {
      const target = this.host.getElement(hoveredId);
      if (target) outline(target, 'outline-hover');
    }

    for (const id of selectedIds) {
      if (this.host.isStop(id)) {
        for (const user of this.host.getGradientUsers(id)) outline(user, 'outline-gradient');
        continue;
      }
      const target = this.host.getElement(id);
      if (target) outline(target, 'outline-selected');
    }

    this.drawMorph(scene.morph, local);
    this.drawGuides(scene, origin);
    for (const ruler of scene.rulers) this.drawRuler(ruler, local);

    const single = selectedIds.length === 1 ? selectedIds[0] : undefined;
    if (single !== undefined && !this.host.isStop(single)) {
      const target = this.host.getElement(single);
      const quad = target ? this.host.getQuad(target) : null;
      if (quad) this.drawHandles(single, quad.map(local));
    }
    for (const id of selectedIds) {
      if (!this.host.isStop(id)) this.drawOriginMarker(id, local);
    }
    // Motion keys sit on top of the pivot marker, which covers the key at the playhead.
    this.drawMotion(scene.motion, local);
    this.drawPoints(scene.points, local);
    if (selectedIds.length === 0) this.drawArtboardHandles(local);
    if (focusedMarker !== null) {
      this.el.querySelector<SVGElement>(`[data-origin-for="${focusedMarker}"]`)?.focus();
    }
    if (scene.readout) this.drawReadout(scene.readout, local);
  }

  /** Draws the eight artboard handles, shown while nothing is selected. */
  private drawArtboardHandles(local: (p: Point) => Point): void {
    const rect = this.host.getArtboardRect();
    if (!rect) return;
    const { x: left, y: top } = local({ x: rect.left, y: rect.top });
    const { x: right, y: bottom } = local({ x: rect.right, y: rect.bottom });
    const middleX = (left + right) / 2;
    const middleY = (top + bottom) / 2;
    const handles: { id: ArtboardHandle; x: number; y: number; cursor: string; name: string }[] = [
      { id: 'nw', x: left, y: top, cursor: 'nwse', name: 'top left' },
      { id: 'n', x: middleX, y: top, cursor: 'ns', name: 'top' },
      { id: 'ne', x: right, y: top, cursor: 'nesw', name: 'top right' },
      { id: 'e', x: right, y: middleY, cursor: 'ew', name: 'right' },
      { id: 'se', x: right, y: bottom, cursor: 'nwse', name: 'bottom right' },
      { id: 's', x: middleX, y: bottom, cursor: 'ns', name: 'bottom' },
      { id: 'sw', x: left, y: bottom, cursor: 'nesw', name: 'bottom left' },
      { id: 'w', x: left, y: middleY, cursor: 'ew', name: 'left' },
    ];
    for (const handle of handles) {
      const square = svgEl(
        'rect',
        {
          class: ['handle', 'handle-artboard', `cursor-${handle.cursor}`],
          x: handle.x - HANDLE_SIZE / 2,
          y: handle.y - HANDLE_SIZE / 2,
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          'data-artboard-handle': handle.id,
        },
        svgEl(
          'title',
          null,
          `Drag to resize the artboard from the ${handle.name} (Shift keeps proportions on a corner, Alt resizes from the centre)`,
        ),
      );
      square.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.host.beginArtboardResize(handle.id, event);
      });
      this.el.append(square);
    }
  }

  /** Draws the transform origin as a ring with a crosshair. It can be focused to nudge it. */
  private drawOriginMarker(id: ElementId, local: (p: Point) => Point): void {
    const point = this.host.getOrigin(id);
    if (!point) return;
    const { x, y } = local(point);
    const marker = svgEl(
      'g',
      {
        class: 'origin-marker',
        tabindex: 0,
        role: 'button',
        'aria-label': 'Transform origin. Arrow keys nudge it.',
        'data-origin-for': id,
      },
      svgEl(
        'title',
        null,
        'Transform origin: drag to move it, double-click to centre it, right-click for more',
      ),
      svgEl('circle', { cx: x, cy: y, r: 12, class: 'origin-hit' }),
      svgEl('circle', { cx: x, cy: y, r: 6, class: 'origin-ring' }),
      svgEl('circle', { cx: x, cy: y, r: 1.75, class: 'origin-dot' }),
      svgEl('path', {
        d: `M${x - 10} ${y}H${x - 4}M${x + 4} ${y}H${x + 10}M${x} ${y - 10}V${y - 4}M${x} ${y + 4}V${y + 10}`,
        class: 'origin-cross',
      }),
    );
    marker.addEventListener('pointerdown', (event) => {
      if (event.button === 0) this.host.beginOriginDrag(id, event);
    });
    marker.addEventListener('dblclick', (event) => {
      event.stopPropagation();
      this.host.resetOrigin(id);
    });
    marker.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.host.showOriginMenu(id, event.clientX, event.clientY);
    });
    this.el.append(marker);
  }

  /** Draws the scale squares and the rotate handle around a single selected element. */
  private drawHandles(id: ElementId, quad: Point[]): void {
    const [nw, ne, se, sw] = quad;
    if (!nw || !ne || !se || !sw) return;
    const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const centre = { x: (nw.x + se.x) / 2, y: (nw.y + se.y) / 2 };
    const handles: { id: HandleId; at: Point; axes: ScaleAxes }[] = [
      { id: 'nw', at: nw, axes: 'both' },
      { id: 'ne', at: ne, axes: 'both' },
      { id: 'se', at: se, axes: 'both' },
      { id: 'sw', at: sw, axes: 'both' },
    ];
    const edges: { id: HandleId; a: Point; b: Point; axes: ScaleAxes }[] = [
      { id: 'n', a: nw, b: ne, axes: 'y' },
      { id: 'e', a: ne, b: se, axes: 'x' },
      { id: 's', a: sw, b: se, axes: 'y' },
      { id: 'w', a: nw, b: sw, axes: 'x' },
    ];
    for (const edge of edges) {
      if (Math.hypot(edge.b.x - edge.a.x, edge.b.y - edge.a.y) > EDGE_HANDLE_MIN_LENGTH) {
        handles.push({ id: edge.id, at: mid(edge.a, edge.b), axes: edge.axes });
      }
    }
    for (const handle of handles) {
      const cursor = resizeCursor(angleOf(centre, handle.at));
      const square = svgEl('rect', {
        class: ['handle', 'handle-scale', `cursor-${cursor}`],
        x: handle.at.x - HANDLE_SIZE / 2,
        y: handle.at.y - HANDLE_SIZE / 2,
        width: HANDLE_SIZE,
        height: HANDLE_SIZE,
        'data-handle': handle.id,
      });
      square.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.host.beginScale(id, handle.axes, event);
      });
      this.el.append(square);
    }

    // The rotate handle sits beyond the top edge, on the side facing away from the box centre.
    const top = mid(nw, ne);
    const edge = direction(nw, ne);
    let normal = { x: -edge.y, y: edge.x };
    if (normal.x * (top.x - centre.x) + normal.y * (top.y - centre.y) < 0) {
      normal = { x: -normal.x, y: -normal.y };
    }
    const knob = {
      x: top.x + normal.x * ROTATE_HANDLE_OFFSET,
      y: top.y + normal.y * ROTATE_HANDLE_OFFSET,
    };
    this.el.append(
      svgEl('line', { class: 'handle-stem', x1: top.x, y1: top.y, x2: knob.x, y2: knob.y }),
    );
    const rotate = svgEl('circle', {
      class: 'handle handle-rotate',
      cx: knob.x,
      cy: knob.y,
      r: 6,
      'data-handle': 'rotate',
    });
    rotate.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.host.beginRotate(id, event);
    });
    this.el.append(rotate);
  }

  /**
   * Draws the sampled motion path, with squares at keyframes that can be dragged and round
   * handles that bend the path through them.
   */
  private drawMotion(motion: MotionPathData | null, local: (p: Point) => Point): void {
    if (!motion || motion.points.length < 2) return;
    const points = motion.points.map(local);
    this.el.append(
      svgEl('polyline', {
        class: 'motion-line',
        points: points.map((p) => `${p.x},${p.y}`).join(' '),
      }),
    );
    for (const p of points)
      this.el.append(svgEl('circle', { class: 'motion-dot', cx: p.x, cy: p.y, r: 1.5 }));
    for (const key of motion.keys) {
      const p = local(key.point);
      for (const side of ['in', 'out'] as const) {
        const end = key[side === 'in' ? 'inHandle' : 'outHandle'];
        if (!end) continue;
        const h = local(end);
        this.el.append(
          svgEl('line', { class: 'motion-handle-line', x1: p.x, y1: p.y, x2: h.x, y2: h.y }),
        );
        const knob = svgEl('circle', {
          class: 'motion-handle',
          cx: h.x,
          cy: h.y,
          r: 4,
          'data-keyframe': key.id,
          'data-side': side,
        });
        knob.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          this.host.beginMotionHandleDrag(motion.elementId, key.id, side, event);
        });
        this.el.append(knob);
      }
    }
    for (const key of motion.keys) {
      const p = local(key.point);
      const square = svgEl('rect', {
        class: 'motion-key',
        x: p.x - 5,
        y: p.y - 5,
        width: 10,
        height: 10,
        'data-keyframe': key.id,
      });
      square.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        // Alt-click switches the path through this keyframe between straight and smooth.
        if (event.altKey) this.host.toggleMotionSmooth(key.id);
        else this.host.beginMotionKeyDrag(motion.elementId, key.id, event);
      });
      this.el.append(square);
    }
  }

  /**
   * Draws the path being edited: its outline (double-click to add a point), the points, and the
   * curve handles of the selected points.
   */
  private drawPoints(points: PointEditData | null, local: (p: Point) => Point): void {
    if (!points) return;
    for (const segment of points.segments) {
      const line = segment.points
        .map(local)
        .map((p) => `${p.x},${p.y}`)
        .join(' ');
      this.el.append(svgEl('polyline', { class: 'point-outline', points: line }));
      const hit = svgEl('polyline', { class: 'point-segment', points: line });
      hit.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });
      hit.addEventListener('dblclick', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.host.insertPoint(segment.ref, event.clientX, event.clientY);
      });
      this.el.append(hit);
    }
    for (const handle of points.handles) {
      const a = local(handle.anchor);
      const b = local(handle.end);
      this.el.append(
        svgEl('line', { class: 'point-handle-line', x1: a.x, y1: a.y, x2: b.x, y2: b.y }),
      );
      const knob = svgEl('circle', { class: 'point-handle', cx: b.x, cy: b.y, r: 4 });
      knob.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.host.beginPointHandleDrag(handle.ref, handle.side, event);
      });
      this.el.append(knob);
    }
    for (const anchor of points.anchors) {
      const p = local(anchor.point);
      const dot = svgEl('rect', {
        class: [
          'point-anchor',
          anchor.selected ? 'is-selected' : '',
          anchor.smooth ? 'is-smooth' : '',
        ]
          .filter(Boolean)
          .join(' '),
        x: p.x - 4,
        y: p.y - 4,
        width: 8,
        height: 8,
        rx: anchor.smooth ? 4 : 0,
      });
      dot.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.host.beginPointDrag(anchor.ref, event);
      });
      this.el.append(dot);
    }
  }

  /** Draws the start points of two morph shapes as numbered dots, with lines between pairs. */
  private drawMorph(morph: MorphGuides | null, local: (p: Point) => Point): void {
    if (!morph) return;
    const group = svgEl('g', { class: 'morph-guides' });
    for (const [from, to] of morph.links) {
      const a = local(from);
      const b = local(to);
      group.append(svgEl('line', { class: 'morph-link', x1: a.x, y1: a.y, x2: b.x, y2: b.y }));
    }
    const dots = (points: Point[], className: string): void => {
      points.forEach((point, index) => {
        const p = local(point);
        group.append(
          svgEl('circle', { class: `morph-dot ${className}`, cx: p.x, cy: p.y, r: 8 }),
          svgEl(
            'text',
            { class: 'morph-dot-label', x: p.x, y: p.y + 3.5, 'text-anchor': 'middle' },
            String(index + 1),
          ),
        );
      });
    };
    dots(morph.before, 'morph-before');
    dots(morph.after, 'morph-after');
    this.el.append(group);
  }

  /** Draws magenta snap guides across the viewport. */
  private drawGuides(scene: OverlayScene, origin: DOMRect): void {
    for (const x of scene.guidesX) {
      this.el.append(
        svgEl('line', {
          class: 'snap-guide',
          x1: x - origin.left,
          y1: 0,
          x2: x - origin.left,
          y2: origin.height,
        }),
      );
    }
    for (const y of scene.guidesY) {
      this.el.append(
        svgEl('line', {
          class: 'snap-guide',
          x1: 0,
          y1: y - origin.top,
          x2: origin.width,
          y2: y - origin.top,
        }),
      );
    }
  }

  /** Draws one target's four measuring lines from the pivot, with pill labels in document units. */
  private drawRuler(ruler: Ruler, local: (p: Point) => Point): void {
    const group = svgEl('g', { class: ['ruler', `ruler-${ruler.target}`] });
    for (const line of ruler.lines) {
      const from = local(line.from);
      const to = local(line.to);
      const along = direction(from, to);
      const across = { x: -along.y, y: along.x };
      const centred =
        line.direction === 'left' || line.direction === 'right' ? ruler.centredX : ruler.centredY;
      group.append(
        svgEl('line', { class: 'ruler-line', x1: from.x, y1: from.y, x2: to.x, y2: to.y }),
        svgEl('line', {
          class: 'ruler-tick',
          x1: to.x - across.x * 5,
          y1: to.y - across.y * 5,
          x2: to.x + across.x * 5,
          y2: to.y + across.y * 5,
        }),
      );
      const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
      const width = line.label.length * 6.4 + 10;
      // Labels sit to the side of the line so they never cover the pivot or the artwork edge.
      const spot = { x: middle.x + across.x * 12, y: middle.y + across.y * 12 };
      group.append(
        svgEl(
          'g',
          { class: ['ruler-label', centred ? 'is-centred' : ''], 'data-direction': line.direction },
          svgEl('title', null, 'SVG pixels, not screen pixels'),
          svgEl('rect', { x: spot.x - width / 2, y: spot.y - 8, width, height: 16, rx: 8 }),
          svgEl('text', { x: spot.x, y: spot.y + 4, 'text-anchor': 'middle' }, line.label),
        ),
      );
    }
    const first = ruler.lines[0];
    if (first) {
      const pivot = local(first.from);
      if (ruler.centredX) {
        group.append(
          svgEl('text', { class: 'ruler-equals', x: pivot.x - 14, y: pivot.y - 8 }, '='),
        );
      }
      if (ruler.centredY) {
        group.append(
          svgEl('text', { class: 'ruler-equals', x: pivot.x + 10, y: pivot.y + 20 }, '='),
        );
      }
    }
    this.el.append(group);
  }

  /** Draws the live value beside the pointer during a drag. */
  private drawReadout(
    readout: { text: string; x: number; y: number },
    local: (p: Point) => Point,
  ): void {
    const p = local(readout);
    const width = readout.text.length * 7 + 12;
    this.el.append(
      svgEl(
        'g',
        { class: 'readout' },
        svgEl('rect', { x: p.x, y: p.y, width, height: 20, rx: 4 }),
        svgEl('text', { x: p.x + 6, y: p.y + 14 }, readout.text),
      ),
    );
  }
}
